import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  inject,
} from "@angular/core";
import { AlertController, NavController } from "@ionic/angular";
import { TranslateModule } from "@ngx-translate/core";
import { Swiper, SwiperOptions } from "swiper";

// Pipes
import { SportsExtractDatePipe } from "@sports-pipes/sports.extract-date.pipe";

// Components
import { SbButtonComponent } from "@sports-components//sb-button/sb-button";
import { SbOverviewScoreSetsComponent } from "@sports-components/sb-overview-score-sets/sb-overview-score-sets";
import { SbSportIconComponent } from "@sports-components/sb-sport-icon/sb-sport-icon.component";

// Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { I_MarqueeItem } from "@sports-models/index";

// Services
import { TrackingService } from "@providers/TrackingService";
import { DeviceService } from "@services/device.service";
import { SportService } from "@sports-services/index";

// Stores
import { NewTicketFacade } from "@sports-features/ticket/stores";
import { TicketItemTypesEnum } from "@sports-pages/ticket/utils/ticket.constants";

@Component({
  selector: "sb-marquee",
  templateUrl: "./sb-marquee.html",
  standalone: true,
  imports: [
    CommonModule,
    SbButtonComponent,
    SportsExtractDatePipe,
    SbOverviewScoreSetsComponent,
    SbSportIconComponent,
    TranslateModule,
  ],
  styleUrls: ["./sb-marquee.scss"],
})
export class SbMarqueeComponent implements OnChanges, AfterViewInit {
  @ViewChildren("list") list: QueryList<any>;
  @ViewChild("swiper") swiperContainer: ElementRef;
  @Input() marqueeData: I_MarqueeItem[];
  marquees: I_MarqueeItem[] = [];
  public swiper;
  config: SwiperOptions = {
    loop: false,
    slidesPerView: "auto",
    observer: true,
    followFinger: true,
    grabCursor: true,
    allowSlidePrev: true,
    allowSlideNext: true,
    simulateTouch: true,
    preventClicks: true,
    preventClicksPropagation: true,
    spaceBetween: 0,
    direction: "horizontal",
    noSwiping: false,
    a11y: undefined,
  };
  globalVars!: GlobalVarsModel;

  _element = inject(ElementRef);
  _renderer = inject(Renderer2);
  extractDate = inject(SportsExtractDatePipe);
  nav = inject(NavController);
  trackingService = inject(TrackingService);
  newTicketFacade = inject(NewTicketFacade);
  sportService = inject(SportService);
  alertCtrl = inject(AlertController);

  isMobile = false;
  isDesktop = true;
  deviceService = inject(DeviceService);

  constructor() {
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnChanges() {
    if (this.marqueeData.length > 0) {
      this.marquees = this.updateMarquee(this.marquees, this.marqueeData);
    }
    if (!this.swiper) return;
    this.swiper.update();
  }

  ngAfterViewInit() {
    this.swiper = new Swiper(this.swiperContainer.nativeElement, this.config);
  }

  updateMarquee(previousMarqueeData: I_MarqueeItem[], nextMarqueeData: I_MarqueeItem[]): I_MarqueeItem[] {
    if (previousMarqueeData.length === 0) return nextMarqueeData;

    return nextMarqueeData.map((marqueeItem) => {
      if (!marqueeItem.Game) return marqueeItem;

      const previousMarqueeItem = previousMarqueeData.find(
        (prevItem) => prevItem.Game?.NodeId === marqueeItem.Game.NodeId,
      );

      if (!previousMarqueeItem) return marqueeItem;

      const updatedResults = marqueeItem.Game.Results.map((result) => {
        const previousResult = previousMarqueeItem.Game.Results.find(
          (prevResult) => prevResult.NodeId === result.NodeId,
        );

        return {
          ...result,
          upOdd: previousResult ? previousResult.Odd < result.Odd : false,
          downOdd: previousResult ? previousResult.Odd > result.Odd : false,
        };
      });

      return {
        ...marqueeItem,
        Game: {
          ...marqueeItem.Game,
          Results: updatedResults,
        },
      };
    });
  }

  navigate(NodeId: string, ParentNodeId: string) {
    this.sportService.deepLinkFromMarquee(ParentNodeId, NodeId);
  }

  async showDismiss(title: string, msg: string) {
    const alertOptions = {
      enableBackdropDismiss: false,
      title: title,
      message: msg,
      buttons: [
        {
          role: "cancel",
          text: "OK",
          handler: () => {
            //nothing, this is just a dismiss
          },
        },
      ],
    };
    const alert = await this.alertCtrl.create(alertOptions);
    alert.present();
  }

  getImgString(team) {
    return `assets/global/img/marquee-jerseys/${team}`;
  }

  /**
   *
   * @param game
   * @param event
   * @param gameName
   * @param type
   */
  bet(game, event, gameName: string, type: string) {
    const data = {
      ...game,
      EventDate: this.extractDate.transform(event.StartDate),
      EventName: `${event.ParticipantHome} - ${event.ParticipantAway}`,
      GameName: gameName,
    };
    this.newTicketFacade.checkSelection(data, data.NodeId, TicketItemTypesEnum.LINE_ITEM);
  }

  getPeriodAndMatchTime(liveData, sportHandle) {
    let periodAndMatchTime = "";

    switch (sportHandle) {
      case "basketball":
        periodAndMatchTime = `${liveData.PeriodName} ${liveData.RemainingPeriodTime}`;
        break;
      case "tennis":
        periodAndMatchTime = `${liveData.PeriodName}`;
        break;
      default:
        periodAndMatchTime = `${liveData.PeriodName} '${liveData.MatchTime}`;
        break;
    }

    return periodAndMatchTime;
  }

  isLiveTennis(sportHandle, isLive) {
    return isLive && sportHandle === "tennis";
  }

  trackBy(index, item) {
    return `${item?.NodeId}${item?.ParentNodeId}`;
  }
}
